import type { ReactNode } from 'react'

import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import union from 'lodash/union'
import xorBy from 'lodash/xorBy'
import { renderToString } from 'react-dom/server'

import { ALL_IDS } from 'const/reducerKeys'

/**
 * Customizer function used in conjunction with lodash mergeWith
 * to handle the use case of merging two arrays.
 *
 * Lodash merge by default always replaces objValue with srcValue,
 * but we have a special case in our entities ALL_IDS property
 * where we want the union of the two arrays returned from merge.
 */
export const mergeArray = (
    objValue: unknown,
    srcValue: unknown,
    key: string
): unknown => {
    if (isArray(objValue)) {
        if (key === ALL_IDS) {
            return union(objValue, srcValue as unknown[])
        }
        return srcValue
    }

    return undefined
}

/**
 * Checks if value is unset, that is, null or undefined
 */
export const isUnset = (value: unknown): value is null | undefined =>
    isUndefined(value) || isNull(value)

export function toFiniteNumber(value: unknown): number {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
}

/**
 * Renders something that may not be a string into a string
 */
export const elementToString = (maybeElement: ReactNode | string): string => {
    if (typeof maybeElement === 'string') {
        return maybeElement
    }

    return renderToString(maybeElement)
}

/**
 * Compare 2 arrays by iteratee
 */
export const isArrayEqualBy = <T>(
    values1: T[],
    values2: T[],
    iteratee: string | ((value: T) => unknown)
): boolean => isEmpty(xorBy(values1, values2, iteratee))
